/**
 * Push settings (ARCHITECTURE.md §8, §9).
 *
 * Push is what unlocks **approval-required** strategies: an approval tick sends a
 * push with a single-use token that the mobile app turns into an approve/reject.
 * This page (a) stores + verifies the optional Expo "enhanced push security"
 * access token (`push_expo` credential) — a successful probe unlocks the `push`
 * gate — and (b) lays out the hard, un-skippable prerequisites the operator must
 * satisfy in EAS. Device registration itself happens from the mobile app (only it
 * can mint an Expo push token).
 */
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@richrich/ui";
import { useI18n } from "@richrich/i18n/react";
import type { MessageKey } from "@richrich/i18n";
import type { PutCredential } from "@richrich/contracts";
import { PageShell } from "../../components/PageShell";
import { settingsQuery } from "../../lib/query";
import { credentialsQuery } from "../../features/settings/api";
import { usePutCredential } from "../../features/settings/hooks";
import { Button, ErrorNote, SectionCard, TextField } from "../../components/ui/controls";
import { CredentialActions, CredentialStatus } from "../../features/settings/CredentialControls";

export const Route = createFileRoute("/settings/push")({
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(settingsQuery),
      context.queryClient.ensureQueryData(credentialsQuery),
    ]);
  },
  component: PushSettingsPage,
});

// Copy fields hold i18n keys (resolved in-render via `t()`), never literals.
const PREREQUISITES: readonly { titleKey: MessageKey; detailKey: MessageKey }[] = [
  {
    titleKey: "settings.prereqAndroidTitle",
    detailKey: "settings.prereqAndroidDetail",
  },
  {
    titleKey: "settings.prereqIosTitle",
    detailKey: "settings.prereqIosDetail",
  },
  {
    titleKey: "settings.prereqDeviceTitle",
    detailKey: "settings.prereqDeviceDetail",
  },
];

function PushSettingsPage() {
  const { t } = useI18n();
  const settings = useQuery(settingsQuery);
  const credentials = useQuery(credentialsQuery);
  const summary = credentials.data?.credentials.find((c) => c.kind === "push_expo");
  const pushReady = settings.data?.features.push === true;

  const put = usePutCredential("push_expo");
  const [token, setToken] = useState("");

  function submit() {
    if (token.trim().length === 0) return;
    const body: PutCredential = { authType: "api_key", apiKey: token.trim() };
    put.mutate(body, { onSuccess: () => setToken("") });
  }

  return (
    <PageShell
      eyebrow={t("settings.pushEyebrow")}
      title={t("settings.gatePushTitle")}
      description={t("settings.pushDescription")}
      loading={credentials.isLoading}
      actions={
        pushReady ? (
          <Badge tone="good" dot>
            {t("settings.pushUnlocked")}
          </Badge>
        ) : (
          <Badge tone="neutral" dot>
            {t("settings.pushLocked")}
          </Badge>
        )
      }
    >
      <div className="flex flex-col gap-6">
        <SectionCard
          title={t("settings.expoTokenTitle")}
          description={t("settings.expoTokenDescription")}
          aside={<CredentialStatus summary={summary} />}
        >
          <div className="flex flex-col gap-4">
            <TextField
              label={t("settings.accessTokenLabel")}
              type="password"
              mono
              autoComplete="off"
              placeholder={t("settings.accessTokenPlaceholder")}
              value={token}
              onValueChange={setToken}
              hint={
                summary !== undefined
                  ? t("settings.replaceTokenHint")
                  : t("settings.tokenStoredHint")
              }
            />
            <div className="flex flex-wrap items-center gap-2 border-t border-hairline pt-4">
              <Button
                variant="primary"
                pending={put.isPending}
                disabled={token.trim().length === 0}
                onClick={submit}
              >
                {summary !== undefined ? t("settings.replaceToken") : t("settings.saveToken")}
              </Button>
              <CredentialActions kind="push_expo" summary={summary} />
              {put.isError ? <ErrorNote>{t("settings.saveTokenFailed")}</ErrorNote> : null}
            </div>
          </div>
        </SectionCard>

        <SectionCard
          title={t("settings.prerequisitesTitle")}
          description={t("settings.prerequisitesDescription")}
        >
          <ol className="flex flex-col gap-3">
            {PREREQUISITES.map((item, i) => (
              <li key={item.titleKey} className="flex gap-3">
                <span className="flex size-6 shrink-0 items-center justify-center rounded-full border border-hairline font-mono text-xs text-muted">
                  {i + 1}
                </span>
                <div>
                  <div className="text-sm text-ink">{t(item.titleKey)}</div>
                  <div className="mt-0.5 text-xs text-muted">{t(item.detailKey)}</div>
                </div>
              </li>
            ))}
          </ol>
        </SectionCard>
      </div>
    </PageShell>
  );
}
